/**
 * brdocs — extensible Brazilian-document scanner/extractor.
 *
 * Usage (browser or Lambda):
 *   const decoder = new ZxingDecoder({ wasmLocation: '...' });
 *   const registry = createRegistry(decoder, { photoDecoder });
 *   const { document } = await registry.scan(imagePixels, { mrzText });
 *
 * Grow it by writing a `DocumentParser` and adding it to `register(...)`.
 */
export * from './core/types';
export * from './core/ports';
export { DocumentRegistry } from './core/registry';
export { ZxingDecoder, type ZxingDecoderOptions } from './adapters/zxing-decoder';
export { CnhParser, type PhotoDecoder, type DecodedPhoto } from './parsers/cnh.parser';
export { CinParser } from './parsers/cin.parser';
export { MrzParser } from './parsers/mrz.parser';
export { TextParser } from './parsers/text.parser';
export {
  CinSignatureVerifier,
  StaticKeyProvider,
  type KeyProvider,
} from './verify/cin-verifier';
export {
  CnhSignatureVerifier,
  StaticCertProvider,
  ecPointFromSpki,
  type CertProvider,
  type SerproCertKey,
} from './verify/cnh-verifier';

import { DocumentRegistry } from './core/registry';
import { QrDecoder } from './core/ports';
import { SignatureVerifier } from './core/types';
import { CnhParser, PhotoDecoder } from './parsers/cnh.parser';
import { CinParser } from './parsers/cin.parser';
import { MrzParser } from './parsers/mrz.parser';
import { TextParser } from './parsers/text.parser';
import { CinSignatureVerifier, KeyProvider } from './verify/cin-verifier';
import { CnhSignatureVerifier, CertProvider } from './verify/cnh-verifier';
import { MjspKeyProvider } from './verify/mjsp-key';

export { MJSP_CIN_PUBLIC_KEY, MjspKeyProvider } from './verify/mjsp-key';

// Framework-agnostic OCR-text → normalized-identity-fields extractor (DOM-free,
// I/O-free). Best-effort, tuned per document type. See ./extract for details.
export { extractFields, normalizeCpf, normalizeDate } from './extract';

// Document pre-processing pipeline (port of tools/doc-cnn): localize → bgremove
// → deskew → orient. Pixel core is framework-agnostic; ONNX/Tesseract behind
// injectable adapters. See ./preprocess for the full API.
export * from './preprocess';

// Document-type classifier (port of the docpic CNN classifier) + the
// preprocess-then-classify recognizer. Framework-agnostic; the model is behind
// an injectable adapter, so these stay Node-safe like the rest of this index.
export * from './classify';
export { DocRecognizer } from './recognize';
export type { RecognizeResult, RecognizeOptions } from './recognize';

// NOTE: the easy browser facade (identifyDocument / scanDocument) pulls in the
// DOM, pdfjs and tesseract, so it is NOT re-exported here to keep this index
// Node-import-safe. Import it from the dedicated entry instead:
//   import { identifyDocument, scanDocument } from './browser';

export interface RegistryOptions {
  /** Decodes embedded photos (e.g. CNH BPG) — browser/Node specific. */
  photoDecoder?: PhotoDecoder;
  /** Trusted keys for signature verification (e.g. the MJSP CIN key). */
  keyProvider?: KeyProvider;
  /** Trusted SERPRO certificates for CNH-e signature verification. */
  certProvider?: CertProvider;
  /** Extra verifiers beyond the built-in CIN/CNH ones. */
  verifiers?: SignatureVerifier[];
}

/** Builds a registry with the built-in Brazilian-document parsers + verifiers. */
export function createRegistry(decoder: QrDecoder, opts: RegistryOptions = {}): DocumentRegistry {
  const registry = new DocumentRegistry(decoder).register(
    new CnhParser(opts.photoDecoder),
    new CinParser(),
    new MrzParser(),
    new TextParser(), // fallback — keep last
  );
  // Default to the known MJSP key so CIN signatures verify out of the box.
  registry.registerVerifier(new CinSignatureVerifier(opts.keyProvider ?? new MjspKeyProvider()));
  if (opts.certProvider) {
    registry.registerVerifier(new CnhSignatureVerifier(opts.certProvider));
  }
  if (opts.verifiers?.length) {
    registry.registerVerifier(...opts.verifiers);
  }
  return registry;
}
